import React, { useEffect, useState, useContext } from 'react';
import * as antd from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import * as core from '@dnd-kit/core';
import * as sortable from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import EditorCard from '../content/editorCard';
import AddOnEditorDrawer from './addoneditordrawer';
import getColumnsSort, { AddOnRecord } from './getcolumnssort';
import * as menuaddonservice from '../../services/menuaddonservice';
import type { PointerSensorOptions } from '@dnd-kit/core';
import { MenuDropDownItem } from '../../types/addontypes';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../../auth/authcontext';

const { Option } = antd.Select;

export interface AddOnSortableListProps {
    selectedMaogGuid: string;
}

const AddOnSortableList: React.FC<AddOnSortableListProps> = ({ selectedMaogGuid }) => {
    const [selectedMaogAddOns, setSelectedMaogAddOns] = useState<AddOnRecord[]>([]);
    const [openEditMaogmaol, setOpenEditMaogmaol] = useState(false);
    const [menuAddOnDropDown, setMenuAddOnDropDown] = useState<MenuDropDownItem[]>([]);
    const [selectedAddOnDisplayName, setSelectedAddOnDisplayName] = useState('');
    const [selectedMenuAddOnGuid, setSelectedMenuAddOnGuid] = useState('');
    const [selectedAddOnGuid, setSelectedAddOnGuid] = useState('');
    const [selectedAddOnStartDate, setSelectedAddOnStartDate] = useState<Dayjs | null>(null);
    const [selectedAddOnEndDate, setSelectedAddOnEndDate] = useState<Dayjs | null>(null);
    const [isWarnAddOnDisplayName, setIsWarnAddOnDisplayName] = useState(false);
    const [isWarnMenuAddOn, setIsWarnMenuAddOn] = useState(false);
    const { userId: userGuid } = useAuth();
    
    useEffect(() => {
        bindAddOnsTable(selectedMaogGuid);
        bindMenuAddOns();
    }, [selectedMaogGuid]);

    const bindAddOnsTable = async (id: string) => {
        const maogmaols = await menuaddonservice.fetchMenuAddOnGroupMenuAddOnLinks(id);
        if (maogmaols) {
            setSelectedMaogAddOns(maogmaols);
        }
    };

    class MyPointerSensor extends core.PointerSensor {
        static override activators = [
            {
                eventName: 'onPointerDown' as const,
                handler: (
                    event: { nativeEvent: PointerEvent },
                    options: PointerSensorOptions
                ): boolean => {
                    return event.nativeEvent.isPrimary && event.nativeEvent.button === 0;
                },
            },
        ] satisfies [
            {
                eventName: 'onPointerDown';
                handler: (
                    event: { nativeEvent: PointerEvent },
                    options: PointerSensorOptions
                ) => boolean;
            }
        ];
    }

    const sensors = core.useSensors(
        core.useSensor(MyPointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        core.useSensor(core.KeyboardSensor, {
            coordinateGetter: sortable.sortableKeyboardCoordinates,
        })
    );

    const RowContext = React.createContext<any>({});
    const DragHandle: React.FC<{ className?: string }> = ({ className }) => {
        const { setActivatorNodeRef, listeners } = useContext(RowContext);
        return (
            <antd.Button
                type="text"
                size="small"
                icon={<HolderOutlined />}
                style={{ cursor: 'move' }}
                ref={setActivatorNodeRef}
                {...listeners}
                className={className}
            />
        );
    };

    const handleDragEnd = async ({ active, over }: core.DragEndEvent) => {
        if (active.id !== over?.id) {
            const oldIndex = selectedMaogAddOns.findIndex((item) => item.key === active.id);
            const newIndex = selectedMaogAddOns.findIndex((item) => item.key === over?.id);
            const updatedData = sortable.arrayMove(selectedMaogAddOns, oldIndex, newIndex);

            if (updatedData.length > 0) {
                const sortValues: Record<string, number> = {};
                updatedData.forEach((item, i) => {
                    item.sortOrder = i + 1;
                    sortValues[item.key] = i + 1;
                });

                setSelectedMaogAddOns(updatedData);
                await menuaddonservice.updateAddOnSortOrder(sortValues);
            }
        }
    };

    const DragRow = (props: any) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
            setActivatorNodeRef,
        } = sortable.useSortable({
            id: props['data-row-key'],
        });

        const style = {
            ...props.style,
            transform: CSS.Translate.toString(transform),
            transition,
            cursor: 'move',
            ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        };

        return (
            <RowContext.Provider value={{ setActivatorNodeRef, listeners }}>
                <tr {...props} ref={setNodeRef} style={style} {...attributes} />
            </RowContext.Provider>
        );
    };

    const handleDeleteAddOn = async (id: string) => {
        try {
            const status = await menuaddonservice.deleteAddOnLink(id);
            if (status === 200) {
                antd.notification.success({
                    message: 'Success',
                    description: 'Add-On Deleted',
                });
            }
        } catch {
            antd.notification.error({
                message: 'Error',
                description: 'Error Deleting Add-On',
            });
        } finally {
            bindAddOnsTable(selectedMaogGuid);
        }
    };

    const handleEditAddOn = (record: AddOnRecord) => {
        setOpenEditMaogmaol(true);
        setSelectedAddOnDisplayName(record.displayName);
        setSelectedMenuAddOnGuid(record.menuGuid);
        setSelectedAddOnGuid(record.key);
        setSelectedAddOnStartDate(record.startDate ? dayjs(record.startDate) : null);
        setSelectedAddOnEndDate(record.endDate ? dayjs(record.endDate) : null);
    };

    const { isReadOnly } = useAuth();
    const columnsSort = getColumnsSort(DragHandle, handleEditAddOn, handleDeleteAddOn, isReadOnly);

    const onEditDrawerClose = () => setOpenEditMaogmaol(false);

    const handleAddOnChange = (value: string) => {
        setSelectedMenuAddOnGuid(value);
        if (value !== '') setIsWarnMenuAddOn(false);
    };

    const bindMenuAddOns = async () => {
        if (menuAddOnDropDown.length === 0) {
            const menuAddOns = await menuaddonservice.fetchMenuAddOnDropDown();
            setMenuAddOnDropDown(menuAddOns);
        }
    };

    const handleSaveAddOnLink = async () => {
        let isvalid = true;

        if (selectedAddOnDisplayName === '') {
            setIsWarnAddOnDisplayName(true);
            isvalid = false;
        }

        if (selectedMenuAddOnGuid === '' || selectedMenuAddOnGuid === '(SELECT)') {
            setIsWarnMenuAddOn(true);
            isvalid = false;
        }

        if (!isvalid) return;

        const payload = {
            MenuAddOnGroupGuid: selectedMaogGuid,
            MenuGuid: selectedMenuAddOnGuid,
            DisplayName: selectedAddOnDisplayName,
            Active: true,
            StartDate: selectedAddOnStartDate ? selectedAddOnStartDate : null,
            EndDate: selectedAddOnEndDate ? selectedAddOnEndDate : null,
            CreatedBy: userGuid ?? ''
        };

        try {
            const status =
                selectedAddOnGuid === ''
                    ? await menuaddonservice.addMenuAddOnLink(payload)
                    : await menuaddonservice.updateMenuAddOnLink({ ...payload, Guid: selectedAddOnGuid });

            if (status === 200) {
                antd.notification.success({
                    message: 'Success',
                    description: 'Add-On Properties Updated',
                });
            }
        } catch {
            antd.notification.error({
                message: 'Error',
                description: 'Error Updating Add-On Properties',
            });
        } finally {
            setOpenEditMaogmaol(false);
            bindAddOnsTable(selectedMaogGuid);
        }
    };

    const handleCancelAddOnLink = () => {
        resetAddOns();
        setOpenEditMaogmaol(false);
    };

    const resetAddOns = () => {
        setSelectedAddOnGuid('');
        setSelectedAddOnDisplayName('');
        setSelectedMenuAddOnGuid('');
    };

    const handleAddOnDisplayTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedAddOnDisplayName(e.target.value);
        if (e.target.value !== '') setIsWarnAddOnDisplayName(false);
    };

    const handleNewMenuAddOn = () => {
        if (selectedMaogAddOns.length >= 6) {
            antd.notification.warning({
                message: 'Maximum Limit Reached',
                description: 'You can only add up to 6 menu items in this group.',
            });
            return;
        }

        bindMenuAddOns();
        resetAddOns();
        setOpenEditMaogmaol(true);
    };
    const handleAddOnStartDateChange = (date: Dayjs | null) => {
        setSelectedAddOnStartDate(date ? date : null);
    };
  
    const handleAddOnEndDateChange = (date: Dayjs | null) => {
      setSelectedAddOnEndDate(date ? date : null);
    };

    return (
        <>
            <EditorCard
                title={
                    <antd.Row>
                        <antd.Col span={4} style={{ textAlign: 'left' }}>
                            Group Add-Ons
                        </antd.Col>
                        <antd.Col span={20} style={{ textAlign: 'right' }}>
                            <antd.Button
                                color="green"
                                variant="solid"
                                size="small"
                                onClick={handleNewMenuAddOn}
                            >
                                Add Option
                            </antd.Button>
                        </antd.Col>
                    </antd.Row>
                }
            >
                <antd.Space direction="vertical" size="middle">
                    <antd.Flex>
                        <core.DndContext
                            sensors={sensors}
                            modifiers={[restrictToVerticalAxis]}
                            collisionDetection={core.closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <sortable.SortableContext
                                items={selectedMaogAddOns.map((i) => i.key)}
                                strategy={sortable.verticalListSortingStrategy}
                            >
                                <antd.Table
                                    pagination={false}
                                    columns={columnsSort}
                                    rowKey="key"
                                    dataSource={selectedMaogAddOns}
                                    components={{
                                        body: {
                                            row: (props) => <DragRow {...props} />,
                                        },
                                    }}
                                />
                            </sortable.SortableContext>
                        </core.DndContext>
                    </antd.Flex>
                </antd.Space>
            </EditorCard>

            <AddOnEditorDrawer
                open={openEditMaogmaol}
                onClose={onEditDrawerClose}
                menuAddOnDropDown={menuAddOnDropDown}
                selectedMenuAddOnGuid={selectedMenuAddOnGuid}
                handleAddOnChange={handleAddOnChange}
                isWarnMenuAddOn={isWarnMenuAddOn}
                selectedAddOnDisplayName={selectedAddOnDisplayName}
                handleAddOnDisplayTextChange={handleAddOnDisplayTextChange}
                isWarnAddOnDisplayName={isWarnAddOnDisplayName}
                handleSaveAddOnLink={handleSaveAddOnLink}
                handleCancelAddOnLink={handleCancelAddOnLink}
                selectedAddOnStartDate={selectedAddOnStartDate}
                selectedAddOnEndDate={selectedAddOnEndDate}
                handleAddOnStartDateChange={handleAddOnStartDateChange}
                handleAddOnEndDateChange={handleAddOnEndDateChange}
             
            />
        </>
    );
};

export default AddOnSortableList;